import type { DynamicThemeFix, Theme } from 'darkreader';
import type DarkReader from 'darkreader';
import type { PropsWithChildren } from 'react';
import {
  startTransition,
  useCallback,
  useState,
  useMemo,
  createContext,
  useContext,
  useRef,
} from 'react';
import {
  useCounter,
  useIsClient,
  useIsomorphicLayoutEffect,
  useReadLocalStorage,
} from 'usehooks-ts';

interface DarkModeContextType {
  isDarkMode: boolean;
  enableDarkMode: () => void;
  disableDarkMode: () => void;
}

export const DarkModeContext = createContext<DarkModeContextType>({
  isDarkMode: false,
  enableDarkMode: () =>
    console.error('DarkModeContext has not been initialized'),
  disableDarkMode: () =>
    console.error('DarkModeContext has not been initialized'),
});

export const useDarkMode = () => useContext(DarkModeContext);

export const useIsDarkMode = () => useDarkMode().isDarkMode;

const darkReaderThemeOptions: Partial<Theme> = {
  brightness: 115,
  contrast: 100,
  sepia: 0,
};

export const darkReaderIgnoreInlineStyleClass =
  'darkreader-ignore-inline-style';

const darkReaderFixes: DynamicThemeFix = {
  ignoreInlineStyle: [`.${darkReaderIgnoreInlineStyleClass}`],
} as DynamicThemeFix;

export const DarkModeProvider = ({ children }: PropsWithChildren) => {
  const darkReader = useRef<typeof DarkReader>();

  const isClient = useIsClient();

  const [status, setStatus] = useState<'idle' | 'loading' | 'ready'>('idle');
  const isReady = status === 'ready';

  // Lazy-load DarkReader when the client hydrates
  useIsomorphicLayoutEffect(() => {
    if (darkReader.current || status !== 'idle') return;

    startTransition(() => {
      setStatus('loading');

      import('darkreader').then(module => {
        startTransition(() => {
          darkReader.current = module;
          setStatus('ready');
        });
      });
    });
  }, []);

  // Don't initialize a stored value, just read it if set
  const isDarkModePreferred = useReadLocalStorage<boolean>('darkmode');

  const updates = useCounter();

  // Derive the current dark mode state for consumers
  const isDarkMode = useMemo(() => {
    if (!isClient || !darkReader.current) return false;

    // Use the value directly from DarkReader so it's always stable regardless
    // of what triggered the state update.
    return darkReader.current.isEnabled();
  }, [isClient, updates.count]);

  const enableDarkMode = useCallback(() => {
    darkReader.current?.enable(darkReaderThemeOptions, darkReaderFixes);
    updates.increment();
  }, [darkReader.current]);

  const disableDarkMode = useCallback(() => {
    darkReader.current?.disable();
    updates.increment();
  }, [darkReader.current]);

  const followSystemColorScheme = useCallback(() => {
    darkReader.current?.auto(darkReaderThemeOptions, darkReaderFixes);
    updates.increment();
  }, [darkReader.current]);

  useIsomorphicLayoutEffect(() => {
    // Bypass until after client hydration. Skip if the state hasn't changed.
    if (
      !isClient ||
      !isReady ||
      darkReader.current?.isEnabled() === isDarkModePreferred
    )
      return;

    // Apply the preferred state if set, otherwise use auto mode
    switch (isDarkModePreferred) {
      case true:
        darkReader.current?.auto(false);
        enableDarkMode();
        break;

      case false:
        darkReader.current?.auto(false);
        disableDarkMode();
        break;

      case null:
      default:
        followSystemColorScheme();
        break;
    }
  }, [
    isClient,
    isReady,
    isDarkModePreferred,
    disableDarkMode,
    enableDarkMode,
    followSystemColorScheme,
  ]);

  return (
    <DarkModeContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values -- For some reason this causes a hydration mismatch when memoized
      value={{
        isDarkMode,
        enableDarkMode,
        disableDarkMode,
      }}
    >
      {children}
    </DarkModeContext.Provider>
  );
};

export default DarkModeProvider;
